import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  compose,
  setPropTypes,
  withState,
  withHandlers,
  lifecycle
} from 'recompose';
import { withRouter } from 'react-router';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Table from '@material-ui/core/Table';
import UserDetailTable from '../../components/molecules/UserDetailTable';
import FavoriteShopTableHead from '../../components/molecules/FavoriteShopTableHead';
import FavoriteShopTableBody from '../../components/molecules/FavoriteShopTableBody';
import FollowTableHead from '../../components/molecules/FollowTableHead';
import FollowTableBody from '../../components/molecules/FollowTableBody';
import FollowerTableHead from '../../components/molecules/FollowerTableHead';
import FollowerTableBody from '../../components/molecules/FollowerTableBody';
import TablePagination from '../../components/molecules/TablePagination';
import { getUserDetail } from '../../actions/Users';
import CloseButton from '../../components/atoms/CloseButton';

const styles = {
  root: {
    position: 'absolute',
    width: '85%',
    height: 'auto',
    maxHeight: '80%',
    top: '5%',
    left: '2%',
    backgroundColor: '#fff',
    padding: 28,
    overflow: 'scroll',
    outline: 'none'
  },
  margin: {
    margin: '20px 0 0 0'
  },
  scrollTable: {
    maxHeight: 296,
    overflowX: 'scroll',
    whiteSpace: 'nowrap'
  }
};

const propTypes = {
  classes: PropTypes.object.isRequired
};

const enhance = compose(
  connect(
    state => ({
      name: state.loginInfo.name
    }),
    dispatch => ({
      actions: bindActionCreators({ getUserDetail }, dispatch)
    })
  ),
  setPropTypes(propTypes),
  withState('favoriteShopPage', 'setFavoriteShopPage', 0),
  withState('favoriteShopRowsPerPage', 'setFavoriteShopRowsPerPage', 5),
  withState('followPage', 'setFollowPage', 0),
  withState('followRowsPerPage', 'setFollowRowsPerPage', 5),
  withState('followerPage', 'setFollowerPage', 0),
  withState('followerRowsPerPage', 'setFollowerRowsPerPage', 5),
  withState('favoriteShopOrder', 'setFavoriteShopOrder', 'asc'),
  withState('favoriteShopOrderBy', 'setFavoriteShopOrderBy', 'shopId'),
  withState('followOrder', 'setFollowOrder', 'asc'),
  withState('followOrderBy', 'setFollowOrderBy', 'userId'),
  withState('followerOrder', 'setFollowerOrder', 'asc'),
  withState('followerOrderBy', 'setFollowerOrderBy', 'userId'),
  withHandlers({
    handleChangeFavoriteShopPage: props => (event, favoriteShopPage) => {
      props.setFavoriteShopPage(favoriteShopPage);
    },
    handleChangeFavoriteShopRowsPerPage: props => event => {
      props.setFavoriteShopRowsPerPage(event.target.value);
    },
    handleChangeFollowPage: props => (event, followPage) => {
      props.setFollowPage(followPage);
    },
    handleChangeFollowRowsPerPage: props => event => {
      props.setFollowRowsPerPage(event.target.value);
    },
    handleChangeFollowerPage: props => (event, followerPage) => {
      props.setFollowerPage(followerPage);
    },
    handleChangeFollowerRowsPerPage: props => event => {
      props.setFollowerRowsPerPage(event.target.value);
    },
    handleRequestSort: props => (property, type) => {
      if (type === 'favoriteShop') {
        const isDesc =
          props.favoriteShopOrderBy === property &&
          props.favoriteShopOrder === 'desc';
        props.setFavoriteShopOrder(isDesc ? 'asc' : 'desc');
        props.setFavoriteShopOrderBy(property);
      } else if (type === 'follow') {
        const isDesc =
          props.followOrderBy === property && props.followOrder === 'desc';
        props.setFollowOrder(isDesc ? 'asc' : 'desc');
        props.setFollowOrderBy(property);
      } else if (type === 'follower') {
        const isDesc =
          props.followerOrderBy === property && props.followerOrder === 'desc';
        props.setFollowerOrder(isDesc ? 'asc' : 'desc');
        props.setFollowerOrderBy(property);
      }
    }
  }),
  lifecycle({
    componentDidMount() {}
  })
);

const Main = enhance(
  ({
    classes,
    detail,
    updateOpenDetailModal,
    favoriteShopPage,
    favoriteShopRowsPerPage,
    handleChangeFavoriteShopPage,
    handleChangeFavoriteShopRowsPerPage,
    followPage,
    followRowsPerPage,
    handleChangeFollowPage,
    handleChangeFollowRowsPerPage,
    followerPage,
    followerRowsPerPage,
    favoriteShopOrder,
    favoriteShopOrderBy,
    followOrder,
    followOrderBy,
    followerOrder,
    followerOrderBy,
    handleChangeFollowerPage,
    handleChangeFollowerRowsPerPage,
    handleRequestSort
  }) => {
    const favoriteShopEmptyRows =
      favoriteShopRowsPerPage -
      Math.min(
        favoriteShopRowsPerPage,
        detail.favoriteShops.length - favoriteShopPage * favoriteShopRowsPerPage
      );
    const followEmptyRows =
      followRowsPerPage -
      Math.min(
        followRowsPerPage,
        detail.follows.length - followPage * followRowsPerPage
      );
    const followerEmptyRows =
      followerRowsPerPage -
      Math.min(
        followerRowsPerPage,
        detail.followers.length - followerPage * followerRowsPerPage
      );
    const closeAction = () => updateOpenDetailModal(false);
    return (
      <div className={classes.root}>
        <Typography variant="h6">ユーザ情報詳細</Typography>
        <div>
          <UserDetailTable
            accountId={detail.accountId}
            displayName={detail.displayName}
            email={detail.email}
            cognitoUserId={detail.cognitoUserId}
            status={detail.status}
            stripeCustomerId={detail.stripeCustomerId}
            message={detail.message}
            createdAt={detail.createdAt}
            image={detail.image}
            detail={detail}
          />
        </div>
        <div className={classes.margin}>
          <div>お気に入り店舗</div>
          <div className={classes.scrollTable}>
            <Table stickyHeader>
              <FavoriteShopTableHead
                order={favoriteShopOrder}
                orderBy={favoriteShopOrderBy}
                onRequestSort={handleRequestSort}
              />
              <FavoriteShopTableBody
                favoriteShops={detail.favoriteShops}
                emptyRows={favoriteShopEmptyRows}
                page={favoriteShopPage}
                rowsPerPage={favoriteShopRowsPerPage}
                order={favoriteShopOrder}
                orderBy={favoriteShopOrderBy}
              />
            </Table>
          </div>
          <div>
            <TablePagination
              searchResults={detail.favoriteShops}
              page={favoriteShopPage}
              rowsPerPage={favoriteShopRowsPerPage}
              handleChangePage={handleChangeFavoriteShopPage}
              handleChangeRowsPerPage={handleChangeFavoriteShopRowsPerPage}
            />
          </div>
        </div>
        <div className={classes.margin}>
          <div>フォローユーザ</div>
          <div className={classes.scrollTable}>
            <Table stickyHeader>
              <FollowTableHead
                order={followOrder}
                orderBy={followOrderBy}
                onRequestSort={handleRequestSort}
              />
              <FollowTableBody
                follows={detail.follows}
                emptyRows={followEmptyRows}
                page={followPage}
                rowsPerPage={followRowsPerPage}
                order={followOrder}
                orderBy={followOrderBy}
              />
            </Table>
          </div>
          <div>
            <TablePagination
              searchResults={detail.follows}
              page={followPage}
              rowsPerPage={followRowsPerPage}
              handleChangePage={handleChangeFollowPage}
              handleChangeRowsPerPage={handleChangeFollowRowsPerPage}
            />
          </div>
        </div>
        <div className={classes.margin}>
          <div>フォロワーユーザ</div>
          <div className={classes.scrollTable}>
            <Table stickyHeader>
              <FollowerTableHead
                order={followerOrder}
                orderBy={followerOrderBy}
                onRequestSort={handleRequestSort}
              />
              <FollowerTableBody
                followers={detail.followers}
                emptyRows={followerEmptyRows}
                page={followerPage}
                rowsPerPage={followerRowsPerPage}
                order={followerOrder}
                orderBy={followerOrderBy}
              />
            </Table>
          </div>
          <div>
            <TablePagination
              searchResults={detail.followers}
              page={followerPage}
              rowsPerPage={followerRowsPerPage}
              handleChangePage={handleChangeFollowerPage}
              handleChangeRowsPerPage={handleChangeFollowerRowsPerPage}
            />
          </div>
        </div>
        <div>
          <CloseButton onClickAction={closeAction} />
        </div>
      </div>
    );
  }
);

export default withRouter(withStyles(styles)(Main));
